<script setup>
import { computed } from "vue";
import Text from "@/v2/primitives/Text/Text.vue";
import Icon from "@/v2/primitives/Icon/Icon.vue";

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  expandedItemIds: {
    type: Array,
    required: true,
  },
  isChild: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["onExpandItem", "onSelectItem"]);

function onClick() {
  if (!props.item.children?.length) {
    emit("onSelectItem", props.item);
  } else {
    emit("onExpandItem", props.item);
  }
}

function isItemSelected(item) {
  return props.expandedItemIds.includes(item.id);
}

const componentClasses = computed(() => ({
  "side-menu-item": true,
  "side-menu-item--item-unselected":
    props.expandedItemIds.length &&
    !isItemSelected(props.item) &&
    !props.isChild,
  "side-menu-item--item-selected": isItemSelected(props.item),
}));
</script>

<template>
  <li :id="`side-menu-id-${item.id}`" :class="componentClasses">
    <button class="side-menu-item--text" @click="onClick">
      <Text variant="title3">
        {{ item.name }}
        <Icon
          v-if="item.children?.length"
          class="side-menu-item--icon"
          :name="isItemSelected(item) ? 'chevron-up' : 'chevron-down'"
        />
      </Text>
    </button>

    <div v-show="isItemSelected(item)" class="side-menu-children">
      <SideMenuItem
        v-for="(child, c) in item.children"
        :key="`child-${c}`"
        :item="child"
        :expanded-item-ids="expandedItemIds"
        :is-child="true"
        @on-expand-item="$emit('onExpandItem', $event)"
        @on-select-item="$emit('onSelectItem', $event)"
      />
    </div>
  </li>
</template>

<style scoped lang="scss">
$opacity-selected: 1;
$opacity-unselected: 0.6;

.side-menu-item {
  display: flex;
  color: var(--side-menu-text-color);
  flex-direction: column;
  user-select: none;
  text-wrap: balance;

  &--text {
    font-family: inherit;
    display: flex;
    align-items: center;
    gap: var(--spacing-base-sm);
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
    border-radius: var(--border-radius-small);
    text-align: start;
    inline-size: fit-content;

    &:hover {
      opacity: $opacity-unselected;
    }
  }

  &--icon {
    font-size: 1.5rem;
    margin-left: 0.5rem;
  }

  &--item-unselected {
    opacity: $opacity-unselected;
  }

  &--item-selected {
    opacity: $opacity-selected;
  }
}

.side-menu-children {
  display: flex;
  flex-direction: column;
  margin-left: var(--spacing-base);
}
</style>
